/**
 * Utilità per la formattazione di valori come date, valute, ecc.
 */

/**
 * Formatta un valore numerico come valuta
 * @param {number} amount - L'importo da formattare
 * @param {string} currency - Il codice della valuta (default: EUR)
 * @returns {string} - L'importo formattato con simbolo della valuta
 */
export const formatCurrency = (amount, currency = 'EUR') => {
  // Utilizziamo l'API Intl.NumberFormat per la formattazione localizzata
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

/**
 * Formatta una data in formato localizzato italiano
 * @param {string|Date} date - La data da formattare
 * @param {boolean} includeTime - Se includere anche l'orario (default: false)
 * @returns {string} - La data formattata
 */
export const formatDate = (date, includeTime = false) => {
  if (!date) return '';
  
  // Convertiamo la data in oggetto Date se è una stringa
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Opzioni per la formattazione della data
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  
  // Aggiungiamo le opzioni per l'orario se richiesto
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  
  // Utilizziamo l'API Intl.DateTimeFormat per la formattazione localizzata
  return new Intl.DateTimeFormat('it-IT', options).format(dateObj);
};

/**
 * Ottiene una rappresentazione testuale del tempo trascorso
 * @param {string|Date} date - La data da cui calcolare il tempo trascorso
 * @returns {string} - Stringa che rappresenta il tempo trascorso (es. "2 giorni fa")
 */
export const timeAgo = (date) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  
  const seconds = Math.floor((now - dateObj) / 1000);
  
  // Meno di un minuto
  if (seconds < 60) {
    return 'appena ora';
  }
  
  // Meno di un'ora
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minut${minutes === 1 ? 'o' : 'i'} fa`;
  }
  
  // Meno di un giorno
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} or${hours === 1 ? 'a' : 'e'} fa`;
  }
  
  // Meno di una settimana
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} giorn${days === 1 ? 'o' : 'i'} fa`;
  }
  
  // Meno di un mese
  const weeks = Math.floor(days / 7);
  if (weeks < 4) {
    return `${weeks} settiman${weeks === 1 ? 'a' : 'e'} fa`;
  }
  
  // Meno di un anno
  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} mes${months === 1 ? 'e' : 'i'} fa`;
  }
  
  // Anni
  const years = Math.floor(days / 365);
  return `${years} ann${years === 1 ? 'o' : 'i'} fa`;
};
